import type { WsInboundEnvelope } from "./wsInboundEnvelope";
import { optBool, optCallerId, optFiniteNumber, optString } from "./wsPayloadCoercion";

/** Inbound SIP → agent transfer ringing (consult or blind). */
export interface SipCallTransferWsPayload {
  type: "sip_call_transfer";
  call_id: string;
  call_log_id?: string;
  from_user_id?: number;
  caller_name?: string;
  caller_number?: string;
  target_extension?: string;
  target_name?: string;
  transfer_type?: string;
  livekit_room?: string;
  livekit_token?: string;
  livekit_host?: string;
  client_id?: number;
}

export function parseSipCallTransferPayload(
  envelope: WsInboundEnvelope,
): SipCallTransferWsPayload | null {
  if (envelope.type !== "sip_call_transfer") return null;
  const call_id = optString(envelope.call_id);
  if (!call_id) return null;
  const o = envelope;
  return {
    type: "sip_call_transfer",
    call_id,
    call_log_id: optString(o.call_log_id),
    from_user_id: optCallerId(o.from_user_id),
    caller_name: optString(o.caller_name),
    caller_number: optString(o.caller_number),
    target_extension: optString(o.target_extension),
    target_name: optString(o.target_name),
    transfer_type: optString(o.transfer_type),
    livekit_room: optString(o.livekit_room),
    livekit_token: optString(o.livekit_token),
    livekit_host: optString(o.livekit_host),
    client_id: optFiniteNumber(o.client_id),
  };
}

/** Same user moving an active device call to this session (LiveKit reconnect). */
export interface TransferRequestWsPayload {
  type: "transfer_request";
  call_id: string;
  livekit_room?: string;
  livekit_token?: string;
  livekit_host?: string;
  call_type?: string;
  is_sip_call?: boolean;
  sip_call_log_id?: string;
  provider_call_id?: string;
  target_user_id?: number;
  caller_id?: number;
  target_user_name?: string;
  caller_name?: string;
  client_id?: number;
}

export function parseTransferRequestPayload(
  envelope: WsInboundEnvelope,
): TransferRequestWsPayload | null {
  if (envelope.type !== "transfer_request") return null;
  const call_id = optString(envelope.call_id);
  if (!call_id) return null;
  const o = envelope;
  return {
    type: "transfer_request",
    call_id,
    livekit_room: optString(o.livekit_room),
    livekit_token: optString(o.livekit_token),
    livekit_host: optString(o.livekit_host),
    call_type: optString(o.call_type),
    is_sip_call: optBool(o.is_sip_call),
    sip_call_log_id: optString(o.sip_call_log_id),
    provider_call_id: optString(o.provider_call_id),
    target_user_id: optCallerId(o.target_user_id),
    caller_id: optCallerId(o.caller_id),
    target_user_name: optString(o.target_user_name),
    caller_name: optString(o.caller_name),
    client_id: optFiniteNumber(o.client_id),
  };
}

export interface TransferInProgressWsPayload {
  type: "transfer_in_progress";
  user_id?: number;
  message?: string;
}

export function parseTransferInProgressPayload(
  envelope: WsInboundEnvelope,
): TransferInProgressWsPayload | null {
  if (envelope.type !== "transfer_in_progress") return null;
  return {
    type: "transfer_in_progress",
    user_id: optCallerId(envelope.user_id),
    message: optString(envelope.message),
  };
}

export interface TransferCompleteWsPayload {
  type: "transfer_complete";
  user_id?: number;
  call_id?: string;
}

export function parseTransferCompletePayload(
  envelope: WsInboundEnvelope,
): TransferCompleteWsPayload | null {
  if (envelope.type !== "transfer_complete") return null;
  const o = envelope;
  return {
    type: "transfer_complete",
    user_id: optCallerId(o.user_id),
    call_id: optString(o.call_id),
  };
}

export interface SipTransferCompletedWsPayload {
  type: "sip_transfer_completed";
  call_id?: string;
  call_log_id?: string;
  transfer_type?: string;
  livekit_room?: string;
  target_name?: string;
  target_extension?: string;
}

export function parseSipTransferCompletedPayload(
  envelope: WsInboundEnvelope,
): SipTransferCompletedWsPayload | null {
  if (envelope.type !== "sip_transfer_completed") return null;
  const o = envelope;
  return {
    type: "sip_transfer_completed",
    call_id: optString(o.call_id),
    call_log_id: optString(o.call_log_id),
    transfer_type: optString(o.transfer_type),
    livekit_room: optString(o.livekit_room),
    target_name: optString(o.target_name) ?? optString(o.targetName),
    target_extension:
      optString(o.target_extension) ?? optString(o.targetExtension),
  };
}

/** Transfer cancelled/rejected — sent to the target to dismiss ringing/consult. */
export interface SipTransferCancelledWsPayload {
  type: "sip_transfer_cancelled";
  call_id?: string;
  call_log_id?: string;
  livekit_room?: string;
  reason?: string;
}

export function parseSipTransferCancelledPayload(
  envelope: WsInboundEnvelope,
): SipTransferCancelledWsPayload | null {
  if (envelope.type !== "sip_transfer_cancelled") return null;
  const o = envelope;
  return {
    type: "sip_transfer_cancelled",
    call_id: optString(o.call_id),
    call_log_id: optString(o.call_log_id),
    livekit_room: optString(o.livekit_room),
    reason: optString(o.reason),
  };
}

/** Transfer fully handed off — the target now owns the SIP leg. */
export interface SipTransferFinalizedWsPayload {
  type: "sip_transfer_finalized";
  call_id?: string;
  call_log_id?: string;
  livekit_room?: string;
}

export function parseSipTransferFinalizedPayload(
  envelope: WsInboundEnvelope,
): SipTransferFinalizedWsPayload | null {
  if (envelope.type !== "sip_transfer_finalized") return null;
  const o = envelope;
  return {
    type: "sip_transfer_finalized",
    call_id: optString(o.call_id),
    call_log_id: optString(o.call_log_id),
    livekit_room: optString(o.livekit_room),
  };
}
